import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import axios from "axios";
import axiosInstance from "../../utils/axiosConfig";
import {
  ChevronDown,
  Menu,
  X,
  Upload,
  Download,
  UserCircle,
  LogOut,
  FileText,
  ClipboardList,
} from "lucide-react";
import { ExportPopup } from "./ExportPopup";
import { ImportPopup } from "./ImportPopup";
import PropTypes from 'prop-types';

const ROLES = {
  ADMIN: 'admin',
  PROJECT_ENGINEER: 'project engineer',
  LOCATION_HEAD: 'location head',
  DIRECTOR: 'director',
  PURCHASE_DEPT: 'purchase department',
  PURCHASE_HEAD: 'purchase head',
  STORE_OFFICER: 'store officer',
  STORE_HEAD: 'store head',
  PE_SO: 'pe&so',
  L2_GENERAL: 'l2 general',
};

const allowedMergeRoles = [
  ROLES.ADMIN,
  ROLES.PROJECT_ENGINEER,
  ROLES.DIRECTOR,
  ROLES.LOCATION_HEAD,
  ROLES.PE_SO
];

const Dropdown = ({
  title,
  links,
  isMobile = false,
  onClose,
  isAdmin = false,
  userRole = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const getIndentRoute = (role) => {
    const roleRoutes = {
      [ROLES.LOCATION_HEAD]: "/indent2",
      [ROLES.PE_SO]: "/indent4",
      [ROLES.ADMIN]: "/indent",
      [ROLES.STORE_OFFICER]: "/indent5",
      default: "/indent"
    };

    if ([ROLES.DIRECTOR, ROLES.STORE_HEAD, ROLES.PURCHASE_HEAD, 
      ROLES.PURCHASE_DEPT].includes(role)) {
      return "/indent3";
    }

    return roleRoutes[role] || roleRoutes.default;
  };

  const processedLinks = links.map((link) => {
    if (link.name === "Indents" || link.name === "All Indent view") {
      return {
        ...link,
        href: getIndentRoute(userRole),
      };
    }
    return link;
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && 
          !dropdownRef.current.contains(event.target) && 
          !buttonRef.current?.contains(event.target)) {
        setIsOpen(false);
        setActiveSubMenu(null);
        onClose?.();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setActiveSubMenu(null);
        onClose?.();
        buttonRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setActiveSubMenu(null);
    }
  };

  const handleLinkClick = () => {
    setIsOpen(false);
    setActiveSubMenu(null);
    onClose?.();
  };

  const renderIndentSubMenu = () => {
    const indentRoutes = {
      PE: "/peindent",
      L1: "/l1page",
      L2: "/L2Page",
      PD: "/POPage",
      PH: "/PHPage",
      SO: "/SOSHPage",
      SH: "/SHPage",
    };

    const menuItems = Object.entries(indentRoutes).map(([label, route]) => (
      <Link
        key={label}
        to={route}
        className={`block px-${isMobile ? '8' : '4'} py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100`}
        onClick={handleLinkClick}
        role="menuitem"
      >
        {label}
      </Link>
    ));

    if (isMobile) {
      return <div className="bg-gray-50 py-1" role="menu">{menuItems}</div>;
    }

    return (
      <div 
        className="absolute left-full top-0 w-32 bg-white shadow-lg rounded-lg border border-gray-100 py-1" 
        role="menu"
      >
        {menuItems}
      </div>
    );
  };

  const renderLink = (link) => {
    if (link.name === "Indents" && isAdmin) {
      return (
        <div className={isMobile ? "" : "relative group"}>
          <button
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 flex justify-between items-center"
            onClick={() => {
              if (isMobile) {
                setActiveSubMenu(activeSubMenu === "indents" ? null : "indents");
              }
            }}
            aria-expanded={activeSubMenu === "indents"}
            aria-haspopup="true"
          >
            {link.name}
            <ChevronDown
              className={`w-4 h-4 transform transition-transform duration-200 ${
                activeSubMenu === "indents" ? "rotate-180" : ""
              }`}
            />
          </button>
          {((!isMobile && isAdmin) ||
            (isMobile && activeSubMenu === "indents")) &&
            renderIndentSubMenu()}
        </div>
      );
    }

    return (
      <Link
        to={link.href}
        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
        onClick={handleLinkClick}
        role="menuitem"
      >
        {link.name}
      </Link>
    );
  };

  if (isMobile) {
    return (
      <div ref={dropdownRef} className="border-b border-gray-200">
        <button
          ref={buttonRef}
          onClick={toggleOpen}
          className="w-full flex justify-between items-center px-4 py-3 text-left text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          {title}
          <ChevronDown
            className={`w-5 h-5 transform transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {isOpen && (
          <div className="bg-white" role="menu">
            {processedLinks.map((link, index) => (
              <React.Fragment key={index}>{renderLink(link)}</React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        ref={buttonRef}
        onClick={toggleOpen}
        className="flex items-center text-white hover:text-gray-200 transition-colors duration-200 font-medium px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-800 focus:ring-white"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {title}
        <ChevronDown className="ml-1 w-4 h-4" />
      </button>
      {isOpen && (
        <div 
          className="absolute z-20 mt-2 w-56 bg-white shadow-lg rounded-lg border border-gray-100 py-1"
          role="menu"
        >
          {processedLinks.map((link, index) => (
            <React.Fragment key={index}>{renderLink(link)}</React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = ({
  exportEndpoint,
  importEndpoint,
  currentIndentData,
  customFilename,
  showImportButton = false,
  showExportButton = false,
}) => {
  // Render-time log removed to avoid console spam and noisy re-renders
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [showImportPopup, setShowImportPopup] = useState(false);
  const [activeDesktopDropdown, setActiveDesktopDropdown] = useState(null);
  const [masterAccess, setMasterAccess] = useState({
    LocationAccess: false,
    ProjectAccess: false,
    CategoryAccess: false,
    UnitAccess: false,
    ItemsAccess: false
  });
  
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const { role, id, username } = auth;
  const isAdmin = role === ROLES.ADMIN;
  const isL2General = role === ROLES.L2_GENERAL;

  useEffect(() => {
    const fetchMasterAccess = async () => {
      try {
        // Reduce logging noise in production
        const apiUrl = `admin/my-master-access`;
        
        const token = localStorage.getItem("token");
        if (!token) {
          console.error("No authentication token found");
          return;
        }
        
        const response = await axiosInstance.get(apiUrl);
        
        if (!response.data || typeof response.data !== 'object') {
          console.error("Invalid response format:", response.data);
          throw new Error("Invalid response format from server");
        }
        
        // Process the access values - convert to boolean
        const access = {
          LocationAccess: Boolean(Number(response.data.LocationAccess)),
          ProjectAccess: Boolean(Number(response.data.ProjectAccess)),
          CategoryAccess: Boolean(Number(response.data.CategoryAccess)),
          UnitAccess: Boolean(Number(response.data.UnitAccess)),
          ItemsAccess: Boolean(Number(response.data.ItemsAccess)),
        };
        
        setMasterAccess(access);
      } catch (error) {
        console.error("Error fetching master access:", error);
        setMasterAccess({
          LocationAccess: false,
          ProjectAccess: false,
          CategoryAccess: false,
          UnitAccess: false,
          ItemsAccess: false,
        });
      }
    };

    // Only fetch if we have the necessary auth data
    if (id && role) {
      fetchMasterAccess();
    } else {
      // Waiting for auth data
    }
  }, [id, role, isAdmin]);

  const masterLinks = React.useMemo(() => {
    if (isAdmin) {
      return [
        { name: "Location", href: "/mlocation" },
        { name: "Project", href: "/mproject" },
        { name: "Category", href: "/mcategory" },
        { name: "Unit", href: "/mau" },
        { name: "Items", href: "/mitem" },
      ];
    }
    const links = [];
    if (masterAccess.LocationAccess) links.push({ name: "Location", href: "/mlocation" });
    if (masterAccess.ProjectAccess) links.push({ name: "Project", href: "/mproject" });
    if (masterAccess.CategoryAccess) links.push({ name: "Category", href: "/mcategory" });
    if (masterAccess.UnitAccess) links.push({ name: "Unit", href: "/mau" });
    if (masterAccess.ItemsAccess) links.push({ name: "Items", href: "/mitem" });
    return links;
  }, [isAdmin, masterAccess]);

  const hasMasterAccess = React.useMemo(() => {
    if (isAdmin) return true;
    return Object.values(masterAccess).some(value => value === true);
  }, [isAdmin, masterAccess]);

  const getIndentRoute = () => {
    const userRole = role?.toLowerCase();
    const roleRoutes = {
      [ROLES.LOCATION_HEAD]: "/indent2",
      [ROLES.PE_SO]: "/indent4",
      [ROLES.STORE_OFFICER]: "/indent5",
      default: "/indent"
    };

    if ([ROLES.DIRECTOR, ROLES.STORE_HEAD, ROLES.PURCHASE_HEAD, 
         ROLES.PURCHASE_DEPT].includes(userRole)) {
      return "/indent3";
    }

    return roleRoutes[userRole] || roleRoutes.default;
  };

  const getRoleButtons = () => {
    const roleConfig = {
      [ROLES.PROJECT_ENGINEER]: [{ text: "PE", href: "/peindent" }],
      [ROLES.LOCATION_HEAD]: [{ text: "L1", href: "/l1page" }],
      [ROLES.DIRECTOR]: [{ text: "L2", href: "/L2Page" }],
      [ROLES.PURCHASE_DEPT]: [{ text: "PD", href: "/POPage" }],
      [ROLES.PURCHASE_HEAD]: [{ text: "PH", href: "/PHPage" }],
      [ROLES.STORE_OFFICER]: [{ text: "SO", href: "/IRPage" }],
      [ROLES.STORE_HEAD]: [
        { text: "SH", href: "/SHPage" },
        { text: "SO", href: "/SOSHPage" },
      ],
      [ROLES.PE_SO]: [
        { text: "PE", href: "/peindent" },
        { text: "SO", href: "/IRPage" },
      ],
    };

    return roleConfig[role?.toLowerCase()] || [];
  };

  const handleLogout = () => {
    navigate("/login");
  };

  const closeAllDropdowns = () => {
    setActiveDesktopDropdown(null);
  };

  const roleButtons = getRoleButtons();

  const renderRoleButtons = () => {
    if (!Array.isArray(roleButtons) || roleButtons.length === 0) {
      return null;
    }

    return roleButtons.map((button, index) => (
      <Link
        key={index}
        to={button.href}
        className="bg-white text-[#1e3a8a] px-4 py-2 rounded-md flex items-center hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <ClipboardList className="mr-2 w-4 h-4" />
        {button.text}
      </Link>
    ));
  };

  const indentViewRoute = getIndentRoute();

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50">
        {/* Mobile Header */}
        <header className="bg-[#1e3a8a] shadow-md md:hidden">
          <div className="px-4 flex items-center justify-between h-16">
            <Link
              to="/dashboard"
              className="text-lg font-bold text-white hover:text-gray-200"
            >
              Indent Mgmt
            </Link>

            <div className="flex items-center space-x-3">
              {isL2General ? (
                <Link
                  to="/indent"
                  className="bg-white text-[#1e3a8a] px-3 py-1.5 rounded-md text-sm flex items-center hover:bg-gray-100"
                >
                  <FileText className="mr-1 w-4 h-4" />
                  All Indent View
                </Link>
              ) : (
                <>
                  <Link
                    to={indentViewRoute}
                    className="bg-white text-[#1e3a8a] px-3 py-1.5 rounded-md text-sm flex items-center hover:bg-gray-100"
                  >
                    <FileText className="mr-1 w-4 h-4" />
                    Indents
                  </Link>
                </>
              )}
              <button
                onClick={() => setMobileNavOpen(true)}
                className="text-white p-2 hover:bg-blue-800 rounded-md"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </header>

        {/* Desktop Header */}
        <header className="bg-[#1e3a8a] shadow-md hidden md:block">
          <div className="container mx-auto px-4 flex items-center justify-between h-16">
            <Link
              to="/dashboard"
              className="text-xl font-bold text-white hover:text-gray-200"
              onClick={closeAllDropdowns}
            >
              Indent Management
            </Link>

            <nav className="flex items-center space-x-4">
              <Dropdown
                title="Home"
                links={[
                  { name: "Dashboard", href: "/dashboard" },
                  { name: "Documentation", href: "/documentation" },
                  { name: "Developed By", href: "/developed-by" },
                  { name: "Contact Us", href: "/contact" },
                ]}
                onClose={closeAllDropdowns}
                userRole={role?.toLowerCase()}
              />
              <Dropdown
                title="Entry"
                links={[
                  { name: "Indents", href: indentViewRoute },
                  { name: "All Indent view", href: indentViewRoute },
                  { name: "MIN", href: "/min" },
                  { name: "PO", href: "/po" },
                  { name: "Force Close", href: "/force-close" },
                  { name: "History", href: "/history" },
                  { name: "Indent Approvals", href: "/history/indent_approval" },
                  ...(allowedMergeRoles.includes(role?.toLowerCase()) ? [
                    { name: "Merge Indents", href: "/merge-indent" }
                  ] : []),
                  ...([ROLES.ADMIN, ROLES.DIRECTOR].includes(role?.toLowerCase()) ? [
                    { name: "Batch Indents", href: "/batch-indent" }
                  ] : [])
                ]}
                isAdmin={isAdmin}
                onClose={closeAllDropdowns}
                userRole={role?.toLowerCase()}
              />
              {(isAdmin || hasMasterAccess) && (
                <Dropdown
                  title="Master"
                  links={masterLinks}
                  onClose={closeAllDropdowns}
                  userRole={role?.toLowerCase()}
                />
              )}
              <Dropdown
                title="Report"
                links={[
                  { name: "Indent Open", href: "/indent-open" },
                  { name: "PO Open", href: "/po-open" },
                  { name: "Filter", href: "/admin/query-analyzer" },
                ]}
                onClose={closeAllDropdowns}
                userRole={role?.toLowerCase()}
              />
              {isAdmin && (
                <Link
                  to="/admin"
                  className="text-white hover:text-gray-200 font-medium px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-800 focus:ring-white"
                  onClick={closeAllDropdowns}
                >
                  Admin
                </Link>
              )}
            </nav>

            <div className="flex items-center space-x-4">
              {isL2General ? null : (
                <>
                  <Link
                    to={indentViewRoute}
                    className="bg-white text-[#1e3a8a] px-4 py-2 rounded-md flex items-center hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <FileText className="mr-2 w-4 h-4" />
                    All Indent View
                  </Link>
                  {renderRoleButtons()}
                  {showImportButton && (
                    <button
                      className="bg-white text-[#1e3a8a] px-4 py-2 rounded-md flex items-center hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={() => setShowImportPopup(true)}
                      aria-label="Import data"
                    >
                      <Upload className="mr-2 w-4 h-4" />
                      Import
                    </button>
                  )}
                  {showExportButton && (
                    <button
                      className="bg-white text-[#1e3a8a] px-4 py-2 rounded-md flex items-center hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={() => setShowExportPopup(true)}
                      aria-label="Export data"
                    >
                      <Download className="mr-2 w-4 h-4" />
                      Export
                    </button>
                  )}
                </>
              )}
              <div className="flex items-center space-x-2">
                <div className="text-white">
                  <p className="font-medium">{username}</p>
                  <p className="text-sm">{role}</p>
                </div>
                <button
                  onClick={() => {
                    setDrawerOpen(!drawerOpen);
                    closeAllDropdowns();
                  }}
                  className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-800 focus:ring-white rounded-full"
                  aria-label="Open user menu"
                >
                  <UserCircle className="w-8 h-8 text-white hover:text-gray-200" />
                </button>
              </div>
            </div>
          </div>
        </header>
      </div>

      {/* Spacer */}
      <div className="h-16" aria-hidden="true"></div>

      {/* Mobile Navigation Sidebar */}
      {mobileNavOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
          <div 
            className="fixed right-0 top-0 bottom-0 w-64 bg-white shadow-lg z-[70] overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col h-full">
              <div className="flex justify-between items-center p-4 border-b">
                <div>
                  <h2 className="text-xl font-bold">Menu</h2>
                  <p className="text-sm text-gray-600">{username}</p>
                </div>
                <button
                  className="text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={() => setMobileNavOpen(false)}
                  aria-label="Close menu"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-4 border-b flex flex-wrap gap-2">
                {renderRoleButtons()}
              </div>

              <div className="flex-1 divide-y divide-gray-200 overflow-y-auto">
                <Dropdown
                  title="Home"
                  isMobile
                  links={[
                    { name: "Dashboard", href: "/dashboard" },
                    { name: "Documentation", href: "/documentation" },
                    { name: "Developed By", href: "/developed-by" },
                    { name: "Contact Us", href: "/contact" },
                  ]}
                />
                <Dropdown
                  title="Entry"
                  isMobile
                  links={[
                    { name: "Indents", href: indentViewRoute },
                    { name: "MIN", href: "/min" },
                    { name: "PO", href: "/po" },
                    { name: "Force Close", href: "/force-close" },
                    { name: "History", href: "/history" },
                    { name: "Indent Approvals", href: "/history/indent_approval" },
                    ...(allowedMergeRoles.includes(role?.toLowerCase()) ? [
                      { name: "Merge Indents", href: "/merge-indent" }
                    ] : []),
                    ...([ROLES.ADMIN, ROLES.DIRECTOR].includes(role?.toLowerCase()) ? [
                      { name: "Batch Indents", href: "/batch-indent" }
                    ] : [])
                  ]}
                  isAdmin={isAdmin}
                  userRole={role?.toLowerCase()}
                />
                {(isAdmin || hasMasterAccess) && (
                  <Dropdown
                    title="Master"
                    isMobile
                    links={masterLinks}
                    userRole={role?.toLowerCase()}
                  />
                )}
                <Dropdown
                  title="Report"
                  isMobile
                  links={[
                    { name: "Indent Open", href: "/indent-open" },
                    { name: "PO Open", href: "/po-open" },
                    { name: "Filter", href: "/admin/query-analyzer" },
                  ]}
                  userRole={role?.toLowerCase()}
                />
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="block px-4 py-3 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                  >
                    Admin
                  </Link>
                )}
              </div>

              <div className="sticky bottom-0 left-0 right-0 p-4 border-t bg-white mt-auto">
                <div className="flex space-x-2">
                  {showImportButton && (
                    <button
                      className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md flex items-center justify-center hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={() => setShowImportPopup(true)}
                    >
                      <Upload className="mr-2 w-4 h-4" />
                      Import
                    </button>
                  )}
                  {showExportButton && (
                    <button
                      className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md flex items-center justify-center hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={() => setShowExportPopup(true)}
                    >
                      <Download className="mr-2 w-4 h-4" />
                      Export
                    </button>
                  )}
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full mt-2 flex items-center justify-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Profile Drawer */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div 
            className="fixed inset-y-0 right-0 max-w-xs w-full bg-white shadow-xl z-[70] p-6"
            role="dialog"
            aria-modal="true"
            aria-label="User profile"
          >
            <button
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() => setDrawerOpen(false)}
              aria-label="Close profile"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="mt-12">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <UserCircle className="w-10 h-10 text-gray-600" />
                  <div>
                    <p className="font-medium">{username || "No username found"}</p>
                    <p className="text-sm text-gray-600">{role}</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Import/Export Popups */}
      {showImportPopup && (
        <ImportPopup
          onClose={() => setShowImportPopup(false)}
          importEndpoint={importEndpoint}
        />
      )}

      {showExportPopup && (
        <ExportPopup
          onClose={() => setShowExportPopup(false)}
          exportEndpoint={exportEndpoint}
          indentData={currentIndentData}
          customFilename={customFilename}
        />
      )}
    </>
  );
};

Header.propTypes = {
  exportEndpoint: PropTypes.string,
  importEndpoint: PropTypes.string,
  currentIndentData: PropTypes.array,
  customFilename: PropTypes.string,
  showImportButton: PropTypes.bool,
  showExportButton: PropTypes.bool
};

export default Header;